
import {Exception} from '@/api/interfaces';
import {defineComponent} from "vue";
import {
  CodeDescriptionRestDto,
  SimpleTextWithImageContentRestDto,
  TextWithImageContentRestDto
} from "@/api/pharma-cms-content-block/models";
import {ExtLinkTargetEnum} from "@/api/enums/ext-link-target-enum";
import {StatusEnum} from "@/api/enums/status-enum";
import {mapActions} from 'vuex';
import CbForm from "@/components/layouts/content-blocks/forms/CbForm.vue";
import {emptyButton} from "@/helpers/functions/content_blocks/empty_content";
import {hasButton} from "@/helpers/functions/content_blocks/validation";
import {emptyMultilingualTextField} from "@/helpers/functions/multilingual";
import ability from '@/config/ability.js';
import {TargetPageInfoRestDto} from "@/api/pharma-cms-content-page/models";
import {stringHasContent} from "@/helpers/functions/string";
import BaseLinkPickerInternalLink from "@/components/UI/BaseLinkPickerInternalLink.vue";
import BaseLinkPicker from "@/components/UI/BaseLinkPicker.vue";
import {DpException} from "@/exception/DpException";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {ImageRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageRestDtoModel";
import {BorderTypeEnum} from "@/api/enums/border-type-enum";
import {LightboxLayoutEnum} from "@/api/enums/lightbox-layout-enum";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import InputImage from "@/components/UI/InputImage.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {TextWithImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithImageContentRestDtoModel";
import {
  SimpleTextWithImageContentRestDtoModel
} from "@/models/api/pharma-cms-content-block/SimpleTextWithImageContentRestDtoModel";
import {ContentBlockRestService} from "@/services/rest/cms-content-block/ContentBlockRestService";
import {ContentBlockLibraryRestService} from "@/services/rest/cms-content-block/ContentBlockLibraryRestService";
import {SimpleContentBlockRestService} from "@/services/rest/cms-content-block/SimpleContentBlockRestService";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {FormMixin} from "@/components/base/FormMixin";
import {DpExceptions} from "@/error/DpExceptions";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";

export default defineComponent({
  name: "CbFormTextWithImage",
  mixins: [FormMixin],
  components: {InputImage, InputTextEditor, BaseLinkPicker, BaseLinkPickerInternalLink, CbForm},
  props: {
    cbId: {
      type: String,
      required: false,
    },
    cbType: {
      type: String,
      required: true
    },
    canCopyFromLibrary: {
      type: Boolean,
      required: false,
      default: false,
    },
    contentLibraryCode: {
      type: String,
      required: false,
    },
    docType: {
      type: String,
      required: true,
    },
    isClearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLibrary: {
      type: Boolean,
      required: false,
      default: false,
    },
    isNormalContentBlock: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPublishable: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasButtonSection: {
      type: Boolean,
      required: false,
      default: true,
    },
    mode: {
      type: String,
      required: true,
    },
    returnRoute: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
    },
    description2: {
      type: String,
      required: false,
    },
    description3: {
      type: String,
      required: false,
    },
    description4: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      // The form keeps editing the same content block after a save, so the mode and the id it works
      // with are state of this component from here on, not the properties it was opened with: a save
      // in NEW mode turns the form into an EDIT form for the block it just created.
      currentMode: this.mode as string,
      currentCbId: (this.cbId ?? null) as string | null,
      imageKey: null as string | null,
      btnLinkType: null as string | null,
      linkedBtnContentPage: {} as TargetPageInfoRestDto,
      extLinkOptions: [
        {code: ExtLinkTargetEnum.BLANK, description: this.$t('newWindow')},
        {code: ExtLinkTargetEnum.SELF, description: this.$t('currentWindow')},
      ] as CodeDescriptionRestDto[],
      statusOptions: [
        {code: StatusEnum.PUBLISHED, description: this.$t('published')},
        {code: StatusEnum.NOT_PUBLISHED, description: this.$t('notPublished')},
      ] as CodeDescriptionRestDto[],
      contentBlock: TextWithImageContentRestDtoModel.createWithDefaults() as TextWithImageContentRestDto | SimpleTextWithImageContentRestDto
    };
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
    canLinkInternalPage(): boolean {
      return ability.can('use-feature', 'MGMT_CONTENT_PAGE') && this.cbType === 'SMA_HOMEPAGE_TEXT_WITH_IMAGE';
    },
    hasButton(): boolean {
      return hasButton(this.contentBlock.button);
    },
    hasExternalLink(): boolean {
      return stringHasContent(this.contentBlock.button?.button_url?.nl);
    },
    hasInternalLink(): boolean {
      return (this.contentBlock.button?.button_page_id && stringHasContent(this.contentBlock.button?.button_page_id?.nl)) as boolean;
    },
    isCopiedContent(): boolean {
      //TODO: create logic for determining whether cb is copied or not
      return false;
    },
  },
  methods: {
    ...mapActions('cms_content_page', ['searchContentPageOverviewCb']),
    clearButton() {
      this.contentBlock.button = emptyButton();
      if (this.btnLinkType === 'page') {
        this.linkedBtnContentPage = {};
      }
    },
    async clearContent() {
      if (this.isLibrary || this.isNormalContentBlock) {
        this.contentBlock = TextWithImageContentRestDtoModel.createWithDefaults();
      } else {
        this.contentBlock = SimpleTextWithImageContentRestDtoModel.createWithDefaults();
      }
    },
    async copiedLibContentBlock(): Promise<void> {
      await this.reloadContent();
      await handleSavedSuccessfully();
    },
    determineLinkType() {
      if (this.contentBlock.button?.button_url?.nl) {
        this.btnLinkType = 'url';
      } else if (this.contentBlock.button?.button_page_id?.nl) {
        this.btnLinkType = 'page';
      } else {
        this.btnLinkType = 'url';
      }
    },
    async getLinkedContentPage(): Promise<void> {
      try {
        this.linkedBtnContentPage = await this.searchContentPageOverviewCb(this.contentBlock.button?.button_page_id?.nl);
      } catch (exceptions: unknown) {
        this.setPickerExceptions(exceptions);
      }
    },
    parsePageReferences() {
      if (this.contentBlock.button) {
        switch (this.btnLinkType) {
          case 'url':
            this.contentBlock.button.button_page_id = emptyMultilingualTextField();
            break;
          case 'page':
            this.contentBlock.button.button_url = emptyMultilingualTextField();
            break;
        }
      }
    },
    setLinkType(link_type: string) {
      this.btnLinkType = link_type;
    },
    async setPageId(page_id: string | null) {
      if (!this.contentBlock.button) {
        this.contentBlock.button = emptyButton();
      }

      if (this.contentBlock.button?.button_page_id && page_id) {
        this.contentBlock.button.button_page_id.nl = page_id;
        await this.getLinkedContentPage();
      } else {
        this.contentBlock.button.button_page_id = emptyMultilingualTextField();
      }

    },
    async doSubmit(): Promise<void> {
      if (this.canLinkInternalPage) {
        this.parsePageReferences();
      }

      if ((this.hasInternalLink || this.hasExternalLink) && !stringHasContent(this.contentBlock.button?.button_text?.nl)) {
        throw new DpExceptions([new DpException('Indien je voor de knop een link toevoegt, moet ook de knoptekst worden ingevuld.')]);
      }

      if (this.isLibrary || this.isNormalContentBlock) {
        const contentBlock = this.contentBlock as TextWithImageContentRestDto;

        if (contentBlock.image) {
          contentBlock.image.image_key = new MultilingualRestDtoModel(null, null, null, this.imageKey);
        } else {
          contentBlock.image = new ImageRestDtoModel(
            BorderTypeEnum.NONE,
            new MultilingualRestDtoModel(null, null, null, this.imageKey),
            LightboxLayoutEnum.NONE
          );
        }

        //todo tmp fix
        contentBlock.image_key = null;

        if (this.currentMode === PageDetailModeEnum.NEW) {
          const createdCbId: string | null = this.isLibrary
            ? await ContentBlockLibraryRestService.getInstance()
              .createLibContentBlockTextWithImage(this.contentLibraryCode, contentBlock)
            : await ContentBlockRestService.getInstance()
              .createNormalContentBlockTextWithImage(this.cbType, contentBlock);

          if (createdCbId === null) {
            throw new DpExceptions([new DpException('Het contentblok werd aangemaakt, maar de id ervan werd niet teruggegeven.')]);
          }

          // The form stays open on the block that was just created, so every following save has to
          // update that block instead of creating another one.
          this.currentCbId = createdCbId;
          this.currentMode = PageDetailModeEnum.EDIT;
        } else if (this.currentMode === PageDetailModeEnum.EDIT) {
          await ContentBlockRestService.getInstance()
            .updateNormalContentBlockTextWithImage(this.cbType, this.requireCbId(), contentBlock);
        }
      } else {
        const contentBlock = this.contentBlock as SimpleTextWithImageContentRestDto;

        contentBlock.image_key = new MultilingualRestDtoModel(null, null, null, this.imageKey);
        await SimpleContentBlockRestService.getInstance().updateSimpleContentBlockTextWithImage(this.cbType, contentBlock);
      }
    },
    async doReload(): Promise<void> {
      if (this.isLibrary || this.isNormalContentBlock) {
        if (this.currentMode === PageDetailModeEnum.NEW) {
          this.contentBlock = TextWithImageContentRestDtoModel.createWithDefaults();
          this.imageKey = null;
        } else {
          this.contentBlock = await NormalContentBlockRestService.getInstance()
            .getNormalContentBlockTextWithImage(this.cbType, this.requireCbId());

          this.imageKey = (this.contentBlock as TextWithImageContentRestDto).image?.image_key?.nl ?? null;
        }

        if (this.canLinkInternalPage) {
          this.parsePageReferences();
          this.determineLinkType();
          if (this.btnLinkType === 'page') {
            await this.getLinkedContentPage();
          }
        }
      } else {
        this.contentBlock = await SimpleContentBlockRestService.getInstance()
          .getSimpleContentBlockTextWithImage(this.cbType);
        if (this.contentBlock.image_key?.nl) {
          this.imageKey = this.contentBlock.image_key.nl;
        }
      }

      if (!this.hasButton) {
        this.clearButton();
      }
    },
    requireCbId(): string {
      if (this.currentCbId === null) {
        throw new DpExceptions([new DpException('Er is geen contentblok om te bewaren of op te halen.')]);
      }

      return this.currentCbId;
    },
    /**
     * The content page picker forwards its @exceptions event without a payload, and the
     * cms-content-page axios instance rejects with a bare Exception[]. The FormMixin error handlers
     * recognise neither, and re-throw what they cannot handle.
     */
    setPickerExceptions(exceptions?: unknown): void {
      if (exceptions === undefined || exceptions === null) {
        return;
      }

      this.setExceptions(Array.isArray(exceptions) ? new DpExceptions(exceptions as Exception[]) : exceptions);
    },
  }
});
